import { useEffect, useRef, useState } from 'react';
import { getConfig } from '../config/environment.js';

/**
 * Safely parse a SignalR event payload that may arrive as a JSON string or an object.
 */
export function parseSignalRPayload(data, { allowPlainString = false } = {}) {
  if (data == null) return null;
  if (typeof data === 'object') return data;
  if (typeof data === 'string') {
    const trimmed = data.trim();
    if (!trimmed) return allowPlainString ? data : null;
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        return JSON.parse(trimmed);
      } catch (err) {
        if (allowPlainString) return data;
        throw err;
      }
    }
    if (allowPlainString) return data;
    return JSON.parse(data);
  }
  return data;
}

/**
 * Resilient SignalR hook for real-time draw updates and jackpot events.
 *
 * Features:
 * - Dynamic import of @microsoft/signalr (avoids initial page load overhead).
 * - Internal automatic reconnects: 0s, 2s, 5s, 10s, 20s, 30s via SignalR SDK.
 * - Persistent fallback retry loop if initial start fails or onclose is triggered
 *   when built-in automatic reconnects exhaust.
 * - Reconnect triggers on window 'online' event and document 'visibilitychange' (tab refocus).
 * - Reliable subscription retry logic for organization channels.
 */
export function useSignalR({ onUserPurchase, onDrawStatus, onAdditionalDrawStatus, onUserRegister }) {
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const connectionRef = useRef(null);
  const isUnmountedRef = useRef(false);
  const retryTimeoutRef = useRef(null);
  const retryDelayRef = useRef(5000);

  // Latest handlers, refreshed every render. Listeners read from this ref so
  // we never need to rebind connection.on() when handler identities change.
  const handlersRef = useRef({ onUserPurchase, onDrawStatus, onAdditionalDrawStatus, onUserRegister });
  handlersRef.current = { onUserPurchase, onDrawStatus, onAdditionalDrawStatus, onUserRegister };

  useEffect(() => {
    isUnmountedRef.current = false;
    const config = getConfig();

    if (config.isSocialBot === true) {
      console.log('[SignalR] Social bot detected, skipping connection');
      return undefined;
    }

    if (!config.signalrUrl || !config.tlfId) {
      console.error('[SignalR] Missing signalrUrl or tlfId in config');
      setConnectionStatus('error');
      return undefined;
    }

    let cancelled = false;
    setConnectionStatus('connecting');

    const subscribeToOrg = async (conn, tlfId, retries = 3) => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        if (isUnmountedRef.current || cancelled) return false;
        try {
          await conn.invoke('Subscribe', tlfId);
          return true;
        } catch (err) {
          console.warn(`[SignalR] Subscribe attempt ${attempt}/${retries} failed:`, err?.message || err);
          if (attempt < retries) {
            await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
          }
        }
      }
      return false;
    };

    // Dynamic import keeps SignalR out of the initial bundle/preload
    import('@microsoft/signalr').then(({ HubConnectionBuilder, HubConnectionState, LogLevel }) => {
      if (cancelled || isUnmountedRef.current) return;

      const connection = new HubConnectionBuilder()
        .withUrl(config.signalrUrl)
        .configureLogging(LogLevel.None)
        .withAutomaticReconnect([0, 2000, 5000, 10000, 20000, 30000])
        .build();

      connectionRef.current = connection;

      connection.on('userPurchase', (data) => {
        try {
          const nextDrawData = parseSignalRPayload(data);
          if (nextDrawData) {
            handlersRef.current.onUserPurchase?.(nextDrawData);
          }
        } catch (err) {
          console.error('[SignalR] Error parsing userPurchase data:', err);
        }
      });

      connection.on('drawStatus', (data) => {
        const parsedStatus = parseSignalRPayload(data, { allowPlainString: true });
        handlersRef.current.onDrawStatus?.(parsedStatus);
      });

      connection.on('additionaldrawStatus', (data) => {
        try {
          const additionalDrawData = parseSignalRPayload(data, { allowPlainString: true });
          handlersRef.current.onAdditionalDrawStatus?.(additionalDrawData);
        } catch (err) {
          console.error('[SignalR] Error parsing additionaldrawStatus data:', err);
        }
      });

      connection.on('userRegister', (data) => {
        const parsed = parseSignalRPayload(data, { allowPlainString: true });
        handlersRef.current.onUserRegister?.(parsed);
      });

      const scheduleRetry = () => {
        if (isUnmountedRef.current || cancelled) return;
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);

        const delay = retryDelayRef.current;
        // Exponential backoff capped at 30s: 5s, 10s, 20s, 30s...
        retryDelayRef.current = Math.min(delay * 2, 30000);

        retryTimeoutRef.current = setTimeout(() => {
          retryTimeoutRef.current = null;
          startConnection();
        }, delay);
      };

      connection.onreconnecting(() => {
        if (isUnmountedRef.current || cancelled) return;
        setConnectionStatus('connecting');
      });

      connection.onreconnected(async () => {
        if (isUnmountedRef.current || cancelled) return;
        const subscribed = await subscribeToOrg(connection, config.tlfId);
        if (isUnmountedRef.current || cancelled) return;
        if (subscribed) {
          setConnectionStatus('connected');
          retryDelayRef.current = 5000; // Reset backoff delay on successful reconnect
        } else {
          setConnectionStatus('error');
          connection.stop().catch(() => {});
          scheduleRetry();
        }
      });

      const startConnection = async () => {
        if (isUnmountedRef.current || cancelled) return;
        const conn = connectionRef.current;
        if (!conn) return;

        // Skip if already connecting, connected, or in built-in reconnecting loop
        if (conn.state !== HubConnectionState.Disconnected) return;

        setConnectionStatus('connecting');

        try {
          await conn.start();
          if (cancelled || isUnmountedRef.current) {
            conn.stop().catch(() => {});
            return;
          }
          const subscribed = await subscribeToOrg(conn, config.tlfId);
          if (cancelled || isUnmountedRef.current) {
            conn.stop().catch(() => {});
            return;
          }
          if (subscribed) {
            setConnectionStatus('connected');
            retryDelayRef.current = 5000;
          } else {
            setConnectionStatus('error');
            conn.stop().catch(() => {});
            scheduleRetry();
          }
        } catch (err) {
          if (cancelled || isUnmountedRef.current) return;
          console.warn('[SignalR] Connection start failed:', err?.message || err);
          setConnectionStatus('error');
          scheduleRetry();
        }
      };

      connection.onclose((error) => {
        if (isUnmountedRef.current || cancelled) return;
        setConnectionStatus('disconnected');
        if (error) {
          console.warn('[SignalR] Connection closed unexpectedly:', error);
        }
        // Fallback retry loop when connection closes or auto-reconnect exhausts
        scheduleRetry();
      });

      // Triggers immediate reconnect attempt when browser recovers connectivity or tab becomes visible
      const tryImmediateReconnect = () => {
        if (isUnmountedRef.current || cancelled) return;
        const conn = connectionRef.current;
        if (conn && conn.state === HubConnectionState.Disconnected) {
          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
          }
          retryDelayRef.current = 2000; // Fast retry on network or tab restore
          startConnection();
        }
      };

      window.addEventListener('online', tryImmediateReconnect);
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          tryImmediateReconnect();
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      // Save listeners cleanup reference
      connection._customListenersCleanup = () => {
        window.removeEventListener('online', tryImmediateReconnect);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };

      // Initial start
      startConnection();
    }).catch((err) => {
      if (cancelled || isUnmountedRef.current) return;
      console.error('[SignalR] Failed to load SignalR library:', err);
      setConnectionStatus('error');
    });

    const handleBeforeUnload = () => {
      isUnmountedRef.current = true;
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      if (connectionRef.current) {
        connectionRef.current.stop().catch(() => {});
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      cancelled = true;
      isUnmountedRef.current = true;
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      const conn = connectionRef.current;
      connectionRef.current = null;
      if (conn) {
        conn._customListenersCleanup?.();
        conn.stop().catch(() => {});
      }
    };
  }, []);

  return {
    connectionStatus,
    isConnected: connectionStatus === 'connected'
  };
}
